import { Box } from '@mantine/core'
import { clsx } from '@mantine/styles'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { SearchBar } from '../../common/components/SearchBar/SearchBar'

import { WelcomeHeader } from './WelcomeHeader/WelcomeHeader'
import classes from './Home.module.scss'
import { Upcoming } from './Upcoming/Upcoming'
import { TotalVolunteeredCard } from './TotalVolunteeredCard/TotalVolunteeredCard'
import { SuggestedOpportunitiesRow } from './SuggestedOpportunitiesRow/SuggestedOpportunitiesRow'
import { TotalDonatedCard } from './TotalDonatedCard/TotalDonatedCard'
import { FoundationRewards } from './FoundationRewards/FoundationRewards'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { SuggestedFundraisersRow } from './SuggestedFundraisersRow/SuggestedFundraisersRow'
import { MatchedDonationCard } from './MatchedDonationCard/MatchedDonationCard'
import { WalletBalanceCard } from './WalletBalanceCard/WalletBalanceCard'
import { Carousel, Loader, MultiSlideCarousel, Tab, Tabs } from '@percent/lemonade'
import { useAuth, useGamificationEnabled, useIsMobile, useQuery } from '@percent/workplace-giving/common/hooks'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { DashboardCard } from './DashboardCard/DashboardCard'
import { LatestDonations } from './LatestDonations/LatestDonations'
import { CampaignCard } from '@percent/workplace-giving/common/components/CampaignCard/CampaignCard'
import { getCampaigns } from '@percent/workplace-giving/api/campaigns/getCampaigns/getCampaigns'
import { getPartnerFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { GivingCampaignGroups } from './GivingCampaignGroups/GivingCampaignGroups'
import { GivingActionsRow } from './GivingActionsRow/GivingActionsRow'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { useSearchBar } from './useSearchBar'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'

enum MyImpactTab {
  UPCOMING = 'upcoming',
  LATEST_DONATIONS = 'latest-donations'
}

export function Home() {
  const navigate = useNavigate()
  const has = usePermission()
  const volunteeringEnabled = has(scope('volunteering'))
  const gamificationEnabled = useGamificationEnabled()
  const donationsEnabled = has(scope('donation_link'))
  const donationMatchingEnabled = has(scope('donation_matching'))
  const givingCampaignEnabled = has(scope('giving_campaign'))
  const walletEnabled = has(scope('donation_wallet'))
  const givingAiEnabled = has(scope('giving_ai'))
  const aiSearchEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_AI_SEARCH)
  const givingActionsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_GIVING_ACTIONS)
  const { track } = useAnalytics()
  const { t } = useTranslation()
  const { state } = useAuth()
  const partner = getPartnerFromAuthState(state)!
  const { bannerImg, overlayGradientPercentage, homePageBannerContent } = partner
  const bannerImage = homePageBannerContent?.bannerImage || bannerImg
  const overlayPercentage = overlayGradientPercentage ?? 40
  const showWelcomeHeader = homePageBannerContent?.welcomeCopy ?? true
  const showSearchBar = homePageBannerContent?.searchBar ?? true

  const { data: campaigns, isPending: isLoadingCampaigns } = useQuery(['getCampaigns'], getCampaigns, {
    staleTime: 1000 * 60,
    enabled: givingCampaignEnabled
  })

  const isMobile = useIsMobile()
  const [activeTab, setActiveTab] = useState<MyImpactTab>(MyImpactTab.UPCOMING)

  // Same cards as the desktop column, as carousel slides.
  const metricCards = useMemo(
    () =>
      [
        walletEnabled ? { id: 'wallet-balance', content: <WalletBalanceCard /> } : null,
        donationsEnabled || walletEnabled ? { id: 'total-donated', content: <TotalDonatedCard /> } : null,
        volunteeringEnabled ? { id: 'total-volunteered', content: <TotalVolunteeredCard /> } : null
      ].filter((card): card is { id: string; content: JSX.Element } => card !== null),
    [walletEnabled, donationsEnabled, volunteeringEnabled]
  )
  const items = useMemo(() => {
    return [
      {
        key: MyImpactTab.UPCOMING,
        label: t('workplace_giving.upcoming.title')
      },
      {
        key: MyImpactTab.LATEST_DONATIONS,
        label: t('workplace_giving.latestDonations.title')
      }
    ]
  }, [t])

  const onChangeTab = useCallback((tab: string) => setActiveTab(tab as MyImpactTab), [])

  const searchBarProps = useSearchBar()

  if (givingCampaignEnabled && isLoadingCampaigns) {
    return <Loader />
  }

  return (
    <Box className={classes.PageContainer} data-testid="home-page-wrapper">
      <Box
        data-testid="home-page-banner"
        className={clsx(classes.Banner, {
          [classes.BannerWithOverlay]: !!bannerImage && overlayPercentage > 0
        })}
        style={
          bannerImage
            ? ({
                backgroundImage: `url(${bannerImage})`,
                '--overlay-opacity': overlayPercentage / 100
              } as React.CSSProperties)
            : {}
        }
      >
        {showWelcomeHeader && <WelcomeHeader lightMode={!!bannerImage} />}
        {showSearchBar && (
          <Box className={classes.SearchWrapper} data-testid="home-search-bar">
            <SearchBar
              {...searchBarProps}
              size="medium"
              hideCountrySelector={aiSearchEnabled && givingAiEnabled}
              showAiButton={aiSearchEnabled && givingAiEnabled}
              stackedOnMobile
              onAiClick={query => {
                track({
                  event: 'AI Search Clicked',
                  properties: { origin: 'Home', hasQuery: !!query }
                })
                navigate(AppRoute.AI_SEARCH, { state: query ? { initialMessage: query } : undefined })
              }}
            />
          </Box>
        )}
      </Box>
      <Box className={classes.ContentWrapper}>
        <Box className={classes.CardsWrapper}>
          {volunteeringEnabled && donationsEnabled && (
            <Box>
              <DashboardCard testId="upcoming-and-latest-donations-wrapper" fullHeight>
                <Tabs items={items} activeTab={activeTab} onChangeTab={onChangeTab} scrollable />
                <Tab index={MyImpactTab.UPCOMING} data-testid="upcoming-tab" value={activeTab}>
                  <Upcoming />
                </Tab>
                <Tab
                  index={MyImpactTab.LATEST_DONATIONS}
                  data-testid="latest-donations-tab"
                  value={activeTab}
                  style={{
                    maxHeight: '100%',
                    overflow: 'hidden'
                  }}
                >
                  <LatestDonations />
                </Tab>
              </DashboardCard>
            </Box>
          )}
          {volunteeringEnabled && !donationsEnabled && (
            <DashboardCard testId="upcoming-wrapper" fullHeight>
              <Upcoming removeTab />
            </DashboardCard>
          )}
          {!volunteeringEnabled && donationsEnabled && (
            <DashboardCard testId="latest-donations-wrapper" fullHeight>
              <LatestDonations removeTab />
            </DashboardCard>
          )}
          <Box className={classes.SmallCardsWrapper}>
            {isMobile && (
              <Box className={classes.MetricsCarousel}>
                <MultiSlideCarousel
                  title={t('workplace_giving.home.yourImpact')}
                  itemsPerPage={{ xs: 1.2 }}
                  slideGap="1rem"
                  items={metricCards}
                />
              </Box>
            )}
            {!isMobile && (
              <Box
                className={clsx(classes.TotalsWrapper, {
                  [classes.WithGamification]: gamificationEnabled || donationMatchingEnabled || walletEnabled
                })}
              >
                {walletEnabled && <WalletBalanceCard />}
                {(donationsEnabled || walletEnabled) && <TotalDonatedCard />}
                {volunteeringEnabled && <TotalVolunteeredCard />}
              </Box>
            )}
            {donationMatchingEnabled && !gamificationEnabled && (
              <Box className={classes.GamificationWrapper}>
                <MatchedDonationCard />
              </Box>
            )}
            {gamificationEnabled && (
              <Box className={classes.GamificationWrapper} data-testid="gamification-section">
                <FoundationRewards />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {campaigns?.length ? (
        <Box>
          <Carousel
            title={t('workplace_giving.campaigns.title')}
            items={campaigns.map(campaign => (
              <CampaignCard key={campaign.id} {...campaign} />
            ))}
          />
        </Box>
      ) : null}

      {givingCampaignEnabled && (
        <Box className={classes.PageContent}>
          <GivingCampaignGroups />
        </Box>
      )}

      {givingActionsEnabled && (
        <Box className={classes.PageContent}>
          <GivingActionsRow
            seeAllHref={AppRoute.GIVING_ACTIONS}
            seeAllNavigateOptions={{
              state: { originRoute: AppRoute.HOME, originRouteName: t('workplace_giving.navigation.home') }
            }}
          />
        </Box>
      )}

      {volunteeringEnabled && (
        <Box className={classes.PageContent}>
          <SuggestedOpportunitiesRow
            seeAllNavigateOptions={{
              state: { originRoute: AppRoute.HOME, originRouteName: t('workplace_giving.navigation.home') }
            }}
          />
        </Box>
      )}
      {donationsEnabled && (
        <Box className={classes.PageContent}>
          <SuggestedFundraisersRow
            seeAllNavigateOptions={{
              state: { originRoute: AppRoute.HOME, originRouteName: t('workplace_giving.navigation.home') }
            }}
          />
        </Box>
      )}
    </Box>
  )
}
